/* eslint-disable @typescript-eslint/no-explicit-any */
import { cn } from '@/lib/utils'
import React, { ElementType, ComponentPropsWithoutRef } from 'react'

interface StarBorderProps<T extends ElementType> {
  as?: T
  color?: string
  rounded?: string
  speed?: string
  className?: string
  children: React.ReactNode
}

export function StarBorder<T extends ElementType = 'button'>({
  as,
  className,
  rounded = 'rounded-xl',
  color,
  speed = '3s',
  children,
  ...props
}: StarBorderProps<T> & Omit<ComponentPropsWithoutRef<T>, keyof StarBorderProps<T>>) {
  const Component = as || ('button' as any)
  const defaultColor = color || 'hsl(var(--foreground))'

  return (
    <Component
      className={cn(
        'relative cursor-pointer inline-block py-[1.5px] overflow-hidden w-full',
        rounded
      )}
      {...props}
    >
      <div
        className={cn(
          'absolute w-[300%] h-[50%] bottom-[-11px] right-[-250%] rounded-full animate-star-movement-bottom z-0',
          'opacity-20 dark:opacity-70'
        )}
        style={{
          background: `radial-gradient(circle, ${defaultColor}, transparent 10%)`,
          animationDuration: speed
        }}
      />
      <div
        className={cn(
          'absolute w-[300%] h-[50%] top-[-10px] left-[-250%] rounded-full animate-star-movement-top z-0',
          'opacity-20 dark:opacity-70'
        )}
        style={{
          background: `radial-gradient(circle, ${defaultColor}, transparent 10%)`,
          animationDuration: speed
        }}
      />
      <div
        className={cn(
          'inline-flex items-center text-foreground justify-center whitespace-nowrap text-sm font-medium ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50',
          'relative inline-flex z-1 h-10  border py-2 px-4',
          'bg-gradient-to-b from-background/90 to-muted/90 border-border/40',
          'dark:from-background dark:to-muted dark:border-border',
          rounded,
          className
        )}
      >
        {children}
      </div>
    </Component>
  )
}
